import { useMutation } from '@tanstack/react-query'
import {
  analyzeAttachment,
  analyzeEventAttachment,
  analyzeTempAttachment,
} from '../api/eventAttachments'
import {
  AnalyzeAttachmentRequest,
  AnalyzeAttachmentResponse,
  ExtractedEventDetails,
} from '../types/analysis'

interface UseAttachmentAnalysisOptions {
  onSuccess?: (data: AnalyzeAttachmentResponse) => void
  onError?: (error: Error) => void
}

/**
 * Hook for analyzing any type of attachment
 */
export const useAttachmentAnalysis = (
  options?: UseAttachmentAnalysisOptions
) => {
  return useMutation<
    AnalyzeAttachmentResponse,
    Error,
    AnalyzeAttachmentRequest
  >({
    mutationFn: analyzeAttachment,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  })
}

/**
 * Hook specifically for event attachments
 */
export const useEventAttachmentAnalysis = (
  options?: UseAttachmentAnalysisOptions
) => {
  return useMutation<
    AnalyzeAttachmentResponse,
    Error,
    { eventId: string; attachmentId: string }
  >({
    mutationFn: ({ eventId, attachmentId }) =>
      analyzeEventAttachment(eventId, attachmentId),
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  })
}

/**
 * Hook specifically for temporary attachments
 */
export const useTempAttachmentAnalysis = (
  options?: UseAttachmentAnalysisOptions
) => {
  return useMutation<
    AnalyzeAttachmentResponse,
    Error,
    { attachmentId: string | string[]; createEvent?: boolean }
  >({
    mutationFn: ({ attachmentId, createEvent }) =>
      analyzeTempAttachment(attachmentId, createEvent),
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  })
}

/**
 * Utility function to map API response to form fields
 */
export const mapApiResponseToFormFields = (details: ExtractedEventDetails) => {
  return {
    name: details.name,
    description: details.description || '',
    expectation_statement: details.expectation_statement || '',
    accomplish_statement: details.accomplish_statement || '',
    keywords: details.keywords || [],
    grade_levels: details.grade_levels || [],
    content_areas: details.content_areas || [],
    engagement_opportunities: details.engagement_opportunities || [],
    maximum_attendees: details.maximum_attendees,
    dates: details.dates || [],
    location: details.location || {},
    flexible_dates: details.flexible_dates,
    flexible_times: details.flexible_times,
    flexible_location: details.flexible_location,
    open: false, // Default value since it's not in the API response
  }
}
